import { GetServerSidePropsContext, GetStaticPropsContext } from 'next'
import { ParsedUrlQuery } from 'querystring'

import { defaultLocale, Locale } from './language'

export interface PageUrlOptions {
  newLocale?: Locale
  locale?: Locale
  parameters?: Record<string, string>
}

export enum PageType {
  PAGE = 'page',
  HOME_PAGE = 'homePage',
  BLOG_PAGE = 'blogPage',
  BLOG_POST = 'blogPost',
  BLOG_CATEGORY = 'blogCategory',
  BLOG_AUTHOR = 'blogAuthor',
  CASE = 'case',
}

// export enum Route {
//   HOME = '',
//   BLOG = 'blog',
//   BLOG_CATEGORY = 'blog/category',
//   BLOG_AUTHOR = 'blog/author',
// }

export interface CustomStaticPropsContext<
  Q extends ParsedUrlQuery = ParsedUrlQuery,
> extends GetStaticPropsContext<Q> {
  locale: Locale
  locales: Locale[]
}

export interface CustomErrorStaticPropsContext<
  Q extends ParsedUrlQuery = ParsedUrlQuery,
> extends GetStaticPropsContext<Q> {
  locale?: Locale
  locales: Locale[]
}

export interface CustomServerSidePropsContext<
  Q extends ParsedUrlQuery = ParsedUrlQuery,
> extends GetServerSidePropsContext<Q> {
  locale: Locale
}

export interface StaticPathWithSlug {
  params: {
    slug: string
  }
  locale: Locale
}

export interface StaticPathWithSlugs {
  params: {
    slugs: string[]
  }
  locale: Locale
}

export type PageTypeValues<T> = {
  [K in PageType]: T
}

const pageRoutes: PageTypeValues<string[]> = {
  [PageType.HOME_PAGE]: [],
  [PageType.PAGE]: ['[page_slug]'],
  [PageType.BLOG_PAGE]: ['blog'],
  [PageType.BLOG_POST]: ['blog', '[post_slug]'],
  [PageType.BLOG_CATEGORY]: ['blog', 'category', '[category_slug]'],
  [PageType.BLOG_AUTHOR]: ['blog', 'author', '[author_slug]'],
  [PageType.CASE]: ['case', '[case_slug]'],
}

/**
 * Gets relative page URL.
 */
export const getPageUrl = (
  pageType: PageType,
  slugs: string | string[] | null = null,
  options: PageUrlOptions = {},
) => {
  // Get URL locale prefix
  const localePrefix =
    options?.newLocale &&
    options.newLocale !== options?.locale &&
    options.newLocale !== defaultLocale
      ? `${options.newLocale}`
      : null

  // Get relative URL
  const urlSlugs = slugs && Array.isArray(slugs) ? slugs : [slugs]
  let slugIndex = 0
  const urlSegments =
    pageRoutes[pageType]?.map((segment) => {
      // Replace segment placeholder with a slug or an empty string
      if (/\[.*\]/.test(segment)) {
        slugIndex += 1
        return urlSlugs?.[slugIndex - 1] ?? ''
      }

      return segment
    }) ?? []
  const url = [localePrefix, ...urlSegments].filter(Boolean).join('/')

  // Get URL parameters
  const searchParams = new URLSearchParams()
  Object.entries(options?.parameters ?? {}).forEach((parameter) => {
    if (parameter[0] && parameter[1]) {
      searchParams.set(parameter[0], parameter[1])
    }
  })
  const parameters = searchParams.toString()

  const urlWithParameters = [url, parameters].filter(Boolean).join('?')
  return `/${urlWithParameters}`
}

/**
 * Gets page URL without hash from document.location.
 */
export const getPageLocation = () => {
  const protocol = document.location.protocol
  const hostname = document.location.hostname
  const pathname = document.location.pathname
  const search = document.location.search

  let port = document.location.port
  port = port && port !== '80' ? `:${port}` : ''

  return `${protocol}//${hostname}${port}${pathname}${search}`
}
